import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { RoundedButton } from '../../../elements';
import { listDrives } from '../../../services/drives';
import { listFoldersByTenant } from '../../../services/folders';
import useStore from '../../../hooks/useStore';
import {
  ModalWrapper,
  ModalContent,
  ModalBody,
  ModalFooter,
  FileInfoHeader,
  FileIcon,
  FileDetails,
  FileName,
  FileSize,
  AgentSelectionContainer,
  CurrentAgentRow,
  AgentAvatar,
  AgentInfo,
  AgentName,
  AgentDescription,
  ExpandableHeader,
  AgentList,
  AgentRow,
  ValidationMessage,
  SectionTitle,
  Checkbox,
  TreeContainer,
  SectionDivider
} from './UploadToVaultModal.styled';
import VaultFolderTreeView from './components/VaultFolderTreeView';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const UploadToVaultModal = ({ isOpen, onClose, onUpload, file, currentAgent, availableAgents }) => {
  const { authStore } = useStore();
  
  // Agent selection state
  const [selectedAgentIds, setSelectedAgentIds] = useState(new Set());
  const [showOtherAgents, setShowOtherAgents] = useState(false);
  
  // Drive/Folder selection state
  const [vaults, setVaults] = useState([]);
  const [expandedVaults, setExpandedVaults] = useState(new Set());
  const [vaultFolders, setVaultFolders] = useState({});
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [loadingVaults, setLoadingVaults] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState({});
  const [showDriveFolders, setShowDriveFolders] = useState(false);
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset state and pre-select current agent when modal opens
  useEffect(() => {
    if (isOpen) {
      if (currentAgent) {
        setSelectedAgentIds(new Set([currentAgent.uuid]));
      } else {
        setSelectedAgentIds(new Set());
      }
      setShowOtherAgents(false);
      setSelectedDestinations([]);
      setExpandedVaults(new Set());
      setShowDriveFolders(false);
      fetchVaults();
    }
  }, [isOpen, currentAgent]);

  // Fetch vaults from API
  const fetchVaults = async () => {
    setLoadingVaults(true);
    try {
      console.log('🔍 Fetching vaults for file upload...');
      
      const baseMeta = {
        page: 1,
        page_size: 100,
        order_by: "created_at",
        order_direction: "DESC",
        filters: {}
      };
      
      // Fetch both USER and MANAGE scopes in parallel
      const [userResp, manageResp] = await Promise.all([
        listDrives({ scope: "USER", meta: baseMeta }),
        listDrives({ scope: "MANAGE", meta: baseMeta })
      ]);
      
      const userVaults = userResp?.data?.body || userResp?.data?.data || [];
      const manageVaults = manageResp?.data?.body || manageResp?.data?.data || [];
      const vaultsData = [...userVaults, ...manageVaults];
      
      // Map to normalize vault properties
      const mapped = vaultsData.map((item) => ({
        id: item.vaultUuid || item.vault_uuid || item.id || item.uuid,
        title: item.name || item.title || "Untitled Drive",
        name: item.name || item.title,
        vaultUuid: item.vaultUuid || item.vault_uuid || item.id || item.uuid,
        description: item.description,
        scope: item.scope,
        folderCount: item.folderCount || item.folders_count
      }));
      
      console.log('📁 Total vaults loaded:', mapped.length);
      setVaults(mapped);
    } catch (error) {
      console.error('❌ Error fetching vaults:', error);
      setVaults([]);
    } finally {
      setLoadingVaults(false);
    }
  };

  // Fetch folders for a specific vault
  const fetchFoldersForVault = async (vaultUuid) => {
    setLoadingFolders(prev => ({ ...prev, [vaultUuid]: true }));
    try {
      console.log(`📂 Fetching folders for vault: ${vaultUuid}`);
      
      const foldersBody = {
        parent_folder_uuid: null,
        vault_uuid: vaultUuid,
        page: 1,
        page_size: 100,
        order_by: ["updated_at"],
        order_direction: ["DESC"],
        filters: {},
        cursor: null
      };
      
      const response = await listFoldersByTenant(foldersBody);
      const folders = response.data?.body || [];
      
      console.log(`📁 Loaded ${folders.length} folders for vault ${vaultUuid}`);
      setVaultFolders(prev => ({ ...prev, [vaultUuid]: folders }));
    } catch (error) {
      console.error(`❌ Error fetching folders for vault ${vaultUuid}:`, error);
      setVaultFolders(prev => ({ ...prev, [vaultUuid]: [] }));
    } finally {
      setLoadingFolders(prev => ({ ...prev, [vaultUuid]: false }));
    }
  };

  // Handle vault expansion
  const handleToggleVault = (vaultUuid) => {
    const newExpanded = new Set(expandedVaults);
    if (newExpanded.has(vaultUuid)) {
      newExpanded.delete(vaultUuid);
    } else {
      newExpanded.add(vaultUuid);
      // Fetch folders if not already loaded
      if (!vaultFolders[vaultUuid]) {
        fetchFoldersForVault(vaultUuid);
      }
    }
    setExpandedVaults(newExpanded);
  };

  // Handle destination selection
  const handleToggleDestination = (destination) => {
    setSelectedDestinations(prev => {
      const exists = prev.some(d => 
        d.vaultUuid === destination.vaultUuid && d.folderUuid === destination.folderUuid
      );
      
      if (exists) {
        return prev.filter(d => 
          !(d.vaultUuid === destination.vaultUuid && d.folderUuid === destination.folderUuid)
        );
      } else {
        return [...prev, destination];
      }
    });
  };

  // Handle current agent toggle
  const handleToggleCurrentAgent = () => {
    setSelectedAgentIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(currentAgent.uuid)) {
        // Allow uncheck if drives are selected OR other agents are selected
        if (selectedDestinations.length > 0 || newSet.size > 1) {
          newSet.delete(currentAgent.uuid);
        }
      } else {
        newSet.add(currentAgent.uuid);
      }
      return newSet;
    });
  };

  // Handle other agent selection
  const handleToggleAgent = (agentUuid) => {
    setSelectedAgentIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(agentUuid)) {
        // Allow uncheck if drives are selected OR other agents remain
        if (selectedDestinations.length > 0 || newSet.size > 1) {
          newSet.delete(agentUuid);
        }
      } else {
        newSet.add(agentUuid);
      }
      return newSet;
    });
  };

  if (!isOpen) return null;

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Get other agents (exclude current agent)
  const otherAgents = availableAgents?.filter(agent => agent.uuid !== currentAgent?.uuid) || [];

  const handleSubmit = async (e) => {
    e.stopPropagation();
    
    // At least one destination (drive/folder OR agent) must be selected
    const hasDestinations = selectedDestinations.length > 0;
    const hasAgents = selectedAgentIds.size > 0;
    
    if (!file || (!hasDestinations && !hasAgents)) return;
    
    setIsSubmitting(true);
    try {
      // Get selected agent objects
      const selectedAgents = availableAgents?.filter(agent => selectedAgentIds.has(agent.uuid)) || [];
      await onUpload(file, selectedDestinations, selectedAgents);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (e) => {
    e.stopPropagation();
    onClose();
  };

  // Calculate button text
  const getButtonText = () => {
    if (isSubmitting) return 'Uploading...';
    
    const driveCount = selectedDestinations.length;
    const agentCount = selectedAgentIds.size;
    
    if (driveCount > 0 && agentCount > 0) {
      return `Upload to ${driveCount} drive${driveCount > 1 ? 's' : ''} & assign to ${agentCount} agent${agentCount > 1 ? 's' : ''}`;
    } else if (driveCount > 0) {
      return `Upload to ${driveCount} location${driveCount > 1 ? 's' : ''}`;
    } else if (agentCount > 0) {
      return `Assign to ${agentCount} agent${agentCount > 1 ? 's' : ''}`;
    }
    return 'Upload';
  };

  return (
    <ModalWrapper onClick={(e) => e.stopPropagation()}>
      <ModalContent role="dialog" onClick={(e) => e.stopPropagation()}>
        <ModalBody>
          {file && (
            <FileInfoHeader>
              <FileIcon>
                <InsertDriveFileIcon />
              </FileIcon>
              <FileDetails>
                <FileName title={file.name}>{file.name}</FileName>
                <FileSize>{formatFileSize(file.size)}</FileSize>
              </FileDetails>
            </FileInfoHeader>
          )}
          
          {/* Section 1: Drive/Folder Selection */}
          <AgentSelectionContainer>
            <ExpandableHeader onClick={() => setShowDriveFolders(!showDriveFolders)}>
              {showDriveFolders ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              <span>Upload to specific drive/folder (optional)</span>
            </ExpandableHeader>
            
            {showDriveFolders && (
              <>
                {selectedDestinations.length > 0 && (
                  <div style={{ fontSize: '13px', color: '#007bff', fontWeight: 600, padding: '8px 0' }}>
                    {selectedDestinations.length} location{selectedDestinations.length !== 1 ? 's' : ''} selected
                  </div>
                )}
                
                <TreeContainer>
                  {loadingVaults ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>Loading drives...</div>
                  ) : (
                    <VaultFolderTreeView
                      vaults={vaults}
                      vaultFolders={vaultFolders}
                      expandedVaults={expandedVaults}
                      selectedDestinations={selectedDestinations}
                      loadingFolders={loadingFolders}
                      onToggleVault={handleToggleVault}
                      onToggleDestination={handleToggleDestination}
                    />
                  )}
                </TreeContainer>
              </>
            )}
          </AgentSelectionContainer>
          
          <SectionDivider />
          
          {/* Section 2: Agent Assignment */}
          <AgentSelectionContainer>
            <SectionTitle>Assign to agents (optional):</SectionTitle>
            
            {currentAgent && (
              <CurrentAgentRow>
                <Checkbox
                  type="checkbox"
                  checked={selectedAgentIds.has(currentAgent.uuid)}
                  onChange={handleToggleCurrentAgent}
                />
                <AgentAvatar src={currentAgent.photo_url || ''} alt={currentAgent.name} />
                <AgentInfo>
                  <AgentName>{currentAgent.name}</AgentName>
                  <AgentDescription>Currently chatting with this agent</AgentDescription>
                </AgentInfo>
              </CurrentAgentRow>
            )}
            
            {otherAgents.length > 0 && (
              <>
                <ExpandableHeader onClick={() => setShowOtherAgents(!showOtherAgents)}>
                  {showOtherAgents ? <ExpandMoreIcon /> : <ChevronRightIcon />}
                  <span>Assign to other agents ({otherAgents.length} available)</span>
                </ExpandableHeader>
                
                {showOtherAgents && (
                  <AgentList>
                    {otherAgents.map(agent => (
                      <AgentRow key={agent.uuid}>
                        <Checkbox
                          type="checkbox"
                          checked={selectedAgentIds.has(agent.uuid)}
                          onChange={() => handleToggleAgent(agent.uuid)}
                        />
                        <AgentAvatar src={agent.photo_url || ''} alt={agent.name} />
                        <AgentInfo>
                          <AgentName>{agent.name}</AgentName>
                        </AgentInfo>
                      </AgentRow>
                    ))}
                  </AgentList>
                )}
              </>
            )}
          </AgentSelectionContainer>
          
          {selectedDestinations.length === 0 && selectedAgentIds.size === 0 && (
            <ValidationMessage>Please select at least one drive/folder or agent</ValidationMessage>
          )}
        </ModalBody>
        
        <ModalFooter>
          <RoundedButton
            kind="default"
            outline
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </RoundedButton>
          <RoundedButton
            kind="blue"
            onClick={handleSubmit}
            disabled={isSubmitting || (selectedDestinations.length === 0 && selectedAgentIds.size === 0)}
          >
            {getButtonText()}
          </RoundedButton>
        </ModalFooter>
      </ModalContent>
    </ModalWrapper>
  );
};

UploadToVaultModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  file: PropTypes.object,
  currentAgent: PropTypes.object,
  availableAgents: PropTypes.array,
};

export default UploadToVaultModal;

